import { useMemo } from 'react';
import { useAuth } from './AuthContext';

/**
 * useResourcePermissions('policy_areas') -> { canRead, canCreate, canUpdate,
 * canDelete, canAny }
 *
 * A thin wrapper over AuthContext.can(), so every gated screen checks the
 * same action strings the same way rather than five call sites each risking
 * a typo'd resource/action name with nothing to catch it.
 *
 * `false` here is EXACT for the ten non-hierarchical resources (policy_areas,
 * users, ...) -- the backend computes the map with any_scope=False for them,
 * matching the org-wide-only guard every one of their endpoints actually
 * enforces (see routers/auth.py::_permissions_for). `true` on the four
 * HIERARCHICAL_RESOURCES (documents, hierarchy, workflows, approvals) is only
 * a HINT -- it means "somewhere", not "at the node this button targets" --
 * so hide only on `false`; never treat a `true` there as authorization. The
 * server re-checks against the real target and is the actual authority.
 *
 * FOR A PER-RECORD AFFORDANCE, DO NOT USE THIS -- use the record's own
 * `can_edit` / `can_delete`, via auth/recordRights.js (GitHub issue #181).
 * Document, hierarchy-node, workflow-template and approval-group responses all
 * carry them now, computed server-side against that row's own node. This hook
 * remains right for the MENU-LEVEL question -- "is this screen worth showing
 * at all" -- which is exactly what the global map answers correctly.
 *
 * `canAny` deliberately excludes `approve` and `impersonate` -- neither is a
 * "can you work with this table" signal (a users.impersonate-only role
 * cannot load the user directory, so it must not light up the Users tab).
 */
export function useResourcePermissions(resource) {
  const { can } = useAuth();

  return useMemo(() => {
    const canRead = can(resource, 'read');
    const canCreate = can(resource, 'create');
    const canUpdate = can(resource, 'update');
    const canDelete = can(resource, 'delete');
    return {
      canRead,
      canCreate,
      canUpdate,
      canDelete,
      canAny: canRead || canCreate || canUpdate || canDelete,
    };
  }, [can, resource]);
}
